/**
 * Pipeline API service — maps backend Vacancy API to PipelineJob format.
 * Acts as bridge between /api/v1/vacancy endpoints and the pipeline store.
 */

import { apiClient, ApiResponse } from './api';
import type { PipelineJob, PipelineStatus } from '@/types/pipeline';

interface BackendVacancy {
  id: string;
  userId: string;
  title: string;
  company: string;
  description: string;
  url?: string | null;
  location?: string | null;
  source?: string | null;
  status: string;
  notes: string;
  matchScore?: number | null;
  savedAt: string;
  createdAt: string;
  updatedAt: string;
}

function vacancyToPipelineJob(v: BackendVacancy): PipelineJob {
  return {
    id: v.id,
    vacancyId: v.id,
    title: v.title,
    company: v.company,
    location: v.location || undefined,
    source: v.source || undefined,
    status: (v.status as PipelineStatus) || 'saved',
    matchScore: v.matchScore ?? 0,
    addedAt: v.savedAt || v.createdAt,
    notes: v.notes || '',
    url: v.url || undefined,
    description: v.description || undefined,
  };
}

export interface CreatePipelineJobInput {
  title: string;
  company: string;
  description?: string;
  url?: string;
  location?: string;
  source?: string;
  matchScore?: number;
}

export const pipelineApiService = {
  /**
   * Fetch all jobs from backend
   */
  async fetchJobs(): Promise<PipelineJob[]> {
    const response = await apiClient.get<ApiResponse<{
      vacancies: BackendVacancy[];
      pagination: { total: number };
    }>>('/api/v1/vacancy?limit=100&sortBy=createdAt&sortOrder=desc');

    return (response.data?.data?.vacancies || []).map(vacancyToPipelineJob);
  },

  /**
   * Create a job in backend
   */
  async createJob(input: CreatePipelineJobInput): Promise<PipelineJob> {
    const response = await apiClient.post<ApiResponse<BackendVacancy>>(
      '/api/v1/vacancy',
      {
        title: input.title,
        company: input.company,
        description: input.description || '',
        url: input.url || undefined,
        location: input.location || undefined,
        source: input.source || undefined,
        status: 'saved',
        matchScore: input.matchScore,
      }
    );

    return vacancyToPipelineJob(response.data.data);
  },

  /**
   * Update job status, notes, or other fields
   */
  async updateJob(jobId: string, updates: Partial<{
    status: PipelineStatus;
    notes: string;
    matchScore: number;
    title: string;
    company: string;
    description: string;
    url: string;
    location: string;
  }>): Promise<PipelineJob> {
    const response = await apiClient.put<ApiResponse<BackendVacancy>>(
      `/api/v1/vacancy/${jobId}`,
      updates
    );

    return vacancyToPipelineJob(response.data.data);
  },

  /**
   * Delete a job
   */
  async deleteJob(jobId: string): Promise<void> {
    await apiClient.delete(`/api/v1/vacancy/${jobId}`);
  },
};
